import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Spinner1 from "../common/spinners";
import { ShoppingCart, ScanSearch } from "lucide-react";
import useProfileStore from "../../store/patient/profileStore";
import { toast } from "sonner";

const DEFAULT_LIMIT = 8;

const RadiologyMart = ({
  data,
  loading,
  handleSubmitRequest,
  handleFechAllTest,
  handleQueryTest,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState(null);
  const [date, setDate] = useState();
  const [note, setNote] = useState("");
  const { profile } = useProfileStore();

  const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
  const hasNextPage = Boolean(data?.hasNextPage);

  const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);
tomorrow.setHours(8, 0, 0, 0); // Default to 8am tomorrow

const minDateTime = tomorrow.toISOString().slice(0, 16);

  useEffect(() => {
    const request = {
      q: searchTerm.trim(),
      limit: DEFAULT_LIMIT,
      page: currentPage,
    };

    if (searchTerm.trim()) {
      handleQueryTest(request);
      return;
    }

    handleFechAllTest(request);
  }, [searchTerm, currentPage, handleFechAllTest, handleQueryTest]);

  const handleSubmit = () => {
    console.log(date)
    if (!date) return;

  // Create a date object from the input to check the hours
  const selectedDate = new Date(date);
  const hours = selectedDate.getHours();

  // 1. Validation: Stop if time is after 5 PM (17:00)
  if (hours >= 17) {
    toast.error("Appointments cannot be booked after 5:00 PM. Please select an earlier time.");
    return;
  }
    const formattedDate = date ? `${date}:00+01:00` : null;
    const payload = {
      patient_id: profile?.id,
      requestType: "scans",
      request_note: note,
      urgent: true,
      tests: [selectedRow],
      appointment_date: formattedDate,
    };

    handleSubmitRequest(payload)
      .then(() => {
        toast.success("Request submitted successfully");
        setSelectedRow(null);
        setNote("");
        setDate(null);
      })
      .catch((error) => {
        toast.error(error?.message || "Failed to submit request");
      });
  };

  const pageSummary = useMemo(() => {
    if (!items.length) {
      return "No scan services available";
    }

    return `Page ${currentPage} · ${items.length} scan service${items.length > 1 ? "s" : ""}`;
  }, [currentPage, items]);

  return (
    <div className="w-100">
      <div className="data-panel-head">
        <div className="data-panel-copy">
          <h5>Radiology mart</h5>
          <p>Search scan services live from the API, then move through results page by page.</p>
        </div>
        <div className="data-panel-actions">
          <div className="data-search">
            <Form.Control
              type="text"
              placeholder="Search scans"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="data-table-shell">
        {loading && searchTerm === "" ? (
          <div className="w-100 d-flex justify-content-center py-5">
            <Spinner1 />
          </div>
        ) : items.length === 0 ? (
          <div className="data-table-empty">
            <h6>No radiology services found</h6>
            <p>Try another search term or move back to the first page.</p>
          </div>
        ) : (
          <>
            <div className="data-cell-meta mb-3">{pageSummary}</div>
            <div className="row g-3">
              {items.map((item) => (
                <div key={item?.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                  <div className="data-sidebar-item h-100 d-flex flex-column">
                    <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                      <span className="data-icon-avatar">
                        <ScanSearch size={18} />
                      </span>
                    </div>

                    <div className="data-cell-title text-capitalize mb-2">{item?.name}</div>
                    <div className="data-cell-meta mb-3">
                      {item?.serviceCost?.tariff?.toLocaleString("en-NG", {
                        style: "currency",
                        currency: "NGN",
                      })}
                    </div>

                    <Button
                      variant="outline-primary"
                      className="w-100 mt-auto"
                      size="sm"
                      onClick={() => setSelectedRow(item)}
                    >
                      <ShoppingCart size={16} />
                      &nbsp; Book Scan
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && items.length > 0 && (
          <div className="data-pagination">
            <Button
              variant="light"
              disabled={currentPage === 1 || loading}
              onClick={() => setCurrentPage((page) => page - 1)}
            >
              Previous
            </Button>
            <span>Page {currentPage}</span>
            <Button
              variant="light"
              disabled={!hasNextPage || loading}
              onClick={() => setCurrentPage((page) => page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      <Modal size="lg" show={!!selectedRow} onHide={() => setSelectedRow(null)} scrollable centered>
        <Modal.Header closeButton />
        <Modal.Body>
          {selectedRow ? (
            <div className="w-100">
              <ul style={{ listStyleType: "none", padding: 0 }}>
                <p>Confirm Request</p>
                <li className="mb-4 fw-bold">
                  Title: <span>{selectedRow?.name}</span>
                </li>
                <li className="mb-4">
                  Amount:{" "}
                  <span className="text-primary text-capitalize">
                    {selectedRow?.serviceCost?.tariff?.toLocaleString("en-NG", {
                      style: "currency",
                      currency: "NGN",
                    })}
                  </span>
                </li>
                <div>
                  <p>Please Select a Date</p>
                  <Form.Control
                    type="datetime-local"
                    min={minDateTime}
                    selected={date}
                    onChange={(event) => setDate(event.target.value)}
                    className="mb-3"
                  />
                </div>
                <div>
                  <p>Additional Note (Optional)</p>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    className="mb-3"
                    placeholder="Enter any additional notes here..."
                  />
                </div>
                <Button variant="primary" className="w-100" onClick={handleSubmit} disabled={!date || loading == true}>
                  Confirm & Proceed to Payment
                </Button>
              </ul>
            </div>
          ) : (
            "No data available"
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedRow(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RadiologyMart;
